import * as React from 'react';
import {FunctionComponent} from 'react';
import {IndoorFullWorkout} from '../../../../Models/FullWorkout';
import {classNames} from '../../../Support/classNames';
import {IndoorIntervalTableRow} from './IndoorIntervalTableRow';
import {unrollParts} from '../../../../Business/unrollParts';
import {Duration} from '../../../../Domain/Duration';
import {FeatureFlags} from '../../../../FeatureFlags';
import {useSettings, PowerMode} from '../../../Settings/settings';

import {fullWorkoutPartResultToRangeOverview, RangeInput} from '../../../../Models/RangeInput';
import {MachineConfigurationBadges} from './MachineConfigurationBadges';
import {MachineType, machineTypesContainRp3OrEmpty} from '../../../../Models/MachineType';
import {IndoorIntervalTablePromo} from './IndoorIntervalTablePromo';

interface OwnProps {
    indoorFullWorkout: IndoorFullWorkout;
    highlightedRange?: RangeInput | null;
    onRangeChange?: (range: RangeInput | null) => void;
    className?: string;
}

export const IndoorIntervalTable: FunctionComponent<OwnProps> = ({
    indoorFullWorkout,
    highlightedRange,
    onRangeChange,
    className,
}) => {
    const unrolledParts = unrollParts(indoorFullWorkout);
    const {powerMode} = useSettings();

    if (!FeatureFlags.isAdvanced && !machineTypesContainRp3OrEmpty(indoorFullWorkout.machine_configurations)) {
        return <IndoorIntervalTablePromo className={className} />;
    }

    return (
        <div className={classNames('card intervaltable', className)} id="interval-table">
            <div className="card-title">
                <h5>Intervals</h5>
            </div>
            <table className="table">
                <thead>
                    <tr>
                        {FeatureFlags.rangeAnalyzer && onRangeChange && <th />}
                        <th className="intervaltable__cell">Duration</th>
                        <th className="intervaltable__cell intervaltable__cell--rightalined">
                            Distance <span className="intervaltable__cell-label">(m)</span>
                        </th>
                        <th className="intervaltable__cell intervaltable__cell--rightalined">
                            Ø Split <span className="intervaltable__cell-label">(/500m)</span>
                        </th>
                        <th className="intervaltable__cell intervaltable__cell--rightalined">
                            Ø Stroke rate <span className="intervaltable__cell-label">(/min)</span>
                        </th>
                        <th className="intervaltable__cell intervaltable__cell--rightalined">
                            Ø {powerMode && (powerMode === PowerMode.Split ? 'Split - ' : '')} Power{' '}
                            <span className="intervaltable__cell-label">(W)</span>
                        </th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {indoorFullWorkout.workout_part_results.map((workoutPartResult, key) => {
                        const unrolledPart = unrolledParts && unrolledParts[key] ? unrolledParts[key] : null;

                        //If there is a training plan and this part result is the last one this is an overshoot
                        const isOvershoot =
                            indoorFullWorkout.training_plan !== null &&
                            unrolledPart === null &&
                            key === indoorFullWorkout.workout_part_results.length - 1;

                        //Add all workoutPartResults.elapsed_times together to get the actual start time of this interval
                        const start =
                            key === 0
                                ? Duration.zero()
                                : indoorFullWorkout.workout_part_results.slice(0, key).reduce((acc, partResult) => {
                                      return acc.add(partResult.elapsed_time);
                                  }, Duration.zero());

                        const end = start.add(workoutPartResult.elapsed_time);

                        return (
                            <IndoorIntervalTableRow
                                selected={
                                    !!(
                                        highlightedRange &&
                                        start.equals(highlightedRange.start) &&
                                        end.equals(highlightedRange.end)
                                    )
                                }
                                initiallyExpanded={indoorFullWorkout.workout_part_results.length === 1}
                                fullWorkoutPartResult={workoutPartResult}
                                unrolledPart={unrolledPart}
                                isOvershoot={isOvershoot}
                                onSelectClick={() => {
                                    if (onRangeChange) {
                                        onRangeChange({
                                            start,
                                            end,
                                            predetermined: fullWorkoutPartResultToRangeOverview(workoutPartResult),
                                        });
                                    }
                                }}
                                onDeSelectClick={() => {
                                    if (onRangeChange) {
                                        onRangeChange(null);
                                    }
                                }}
                                key={key}
                            />
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};
